import { css, keyframes } from '@emotion/css';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as Coin } from 'src/assets/images/fortune/ic-nookpoint.svg';
import { ReactComponent as PointDouble } from 'src/assets/images/fortune/point-double.svg';
import { ReactComponent as PointTriple } from 'src/assets/images/fortune/point-triple.svg';
import { TwinkleStars } from 'src/components/shared/TwinkleStars';
import { BalloonShape } from '../../common/BalloonShape';
import { GreenRibbon } from '../../common/GreenRibbon';

const gotPointBackground =
  'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg"%3E%3Cpath d="M74 .013c33.76 0 74-1.623 74 31.987s-40.24 31.987-74 31.987S0 65.61 0 32 40.24.013 74 .013z" fill="%23E0F4BA"/%3E%3C/svg%3E';

const PointRate: React.FC<{ rate: number }> = ({ rate }) => {
  const { t } = useTranslation('fortune');
  switch (rate) {
    case 2:
      return <PointDouble role="img" aria-label={t('aria_double')} />;
    case 3:
      return <PointTriple role="img" aria-label={t('aria_triple')} />;
    default:
      return null;
  }
};

interface PointResultBalloonProps {
  pointFactor: number;
}

const green1 = '#b8f0a4';
const green2 = '#a9f78d';
const size = 24;
const duration = 340;
const stars = [
  { position: { x: '80%', y: '15%' }, color: green1, size, rest: 0, duration },
  { position: { x: '105%', y: '25%' }, color: green2, size, rest: 300, duration },
  { position: { x: '5%', y: '65%' }, color: green2, size, rest: 600, duration },
  { position: { x: '-5%', y: '50%' }, color: green1, size, rest: 900, duration },
  { position: { x: '90%', y: '85%' }, color: green1, size, rest: 1500, duration },
  { position: { x: '100%', y: '75%' }, color: green2, size, rest: 1800, duration },
  { position: { x: '5%', y: '85%' }, color: green2, size, rest: 2100, duration },
  { position: { x: '55%', y: '8%' }, color: green1, size, rest: 2400, duration },
  { position: { x: '90%', y: '95%' }, color: green1, size, rest: 3000, duration },
  { position: { x: '55%', y: '100%' }, color: green2, size, rest: 3300, duration }
];

const bounce = keyframes`
0%, 35%, 44%, 52%, 100% {
  transform: translate(50%, 50%);
}
40% {
  transform: translate(50%, 50%) rotate(-5deg);
}
50% {
  transform: translate(50%, 50%) rotate(-2deg);
}
`;

export const PointResultBalloon: React.FC<PointResultBalloonProps> = ({ pointFactor }) => {
  const { t } = useTranslation('fortune');
  return (
    <div
      className={css`
        position: relative;
        .twinkle {
          margin: 16px auto 32px;
        }
        .todays-point {
          position: relative;
          width: 148px;
          height: 64px;
          background: url('${gotPointBackground}');

          .value {
            display: flex;
            justify-content: center;
            align-items: center;
            height: 100%;
            font-size: 48px;
            line-height: 1.2;
          }
          .rate {
            position: absolute;
            right: 4px;
            bottom: -4px;
            transform-origin: bottom left;
            transform: translate(50%, 50%);
            @media (prefers-reduced-motion: no-preference) {
               animation: ${bounce} 1.2s linear infinite 1s;
            }
          }
        }
        .tomorrow {
          font-size: 12px;
          color: #938666;
          text-align: center;
        }
      `}
    >
      <BalloonShape style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <GreenRibbon>{t('get_point')}</GreenRibbon>
        <div className="twinkle">
          <TwinkleStars stars={stars} delay={500} style={{ padding: '0 32px' }}>
            <div className="todays-point">
              <p className="value">
                <Coin width={36} height={36} />
                10
              </p>
              {pointFactor === 2 || pointFactor === 3 ? (
                <p className="rate">
                  <PointRate rate={pointFactor} />
                </p>
              ) : null}
            </div>
          </TwinkleStars>
        </div>

        <p className="tomorrow">{t('please_come_tomorrow')}</p>
      </BalloonShape>
    </div>
  );
};
